// src/components/RentalDateSelector/RentalDateSelector.jsx
import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { useBooking } from "../../context/BookingContext";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const RentalDateSelector = ({ onDatesChange, isDisabled, labelColor }) => {
  const { selectedDates, setSelectedDates } = useBooking();
  const isFirstRender = useRef(true);

  const now = useRef(new Date());
  now.current.setHours(0, 0, 0, 0);
  const todayDate = now.current.toISOString().split("T")[0];

  const tomorrow = new Date(now.current);
  tomorrow.setDate(now.current.getDate() + 1);
  const tomorrowDate = tomorrow.toISOString().split("T")[0];

  // Aseta oletusarvot, jos niitä ei vielä ole localStoragessa
  useEffect(() => {
    if (!localStorage.getItem("selectedBookingDates")) {
      const defaultDates = {
        startDate: new Date().toISOString(),
        endDate: (() => {
          const d = new Date();
          d.setDate(d.getDate() + 1);
          return d.toISOString();
        })()
      };
      setSelectedDates(defaultDates);
      localStorage.setItem("selectedBookingDates", JSON.stringify(defaultDates));
    }
  }, [setSelectedDates]);

useEffect(() => {
  if (!isFirstRender.current) return;
  isFirstRender.current = false;

  const savedDates = JSON.parse(localStorage.getItem("selectedBookingDates"));
  if (savedDates?.startDate && savedDates?.endDate) {
    const savedStart = new Date(savedDates.startDate);
    savedStart.setHours(0, 0, 0, 0);
    if (savedStart < now.current) {
      const updatedDates = { startDate: todayDate, endDate: tomorrowDate };
      setSelectedDates(updatedDates);
      localStorage.setItem("selectedBookingDates", JSON.stringify(updatedDates));
    } else {
      setSelectedDates(savedDates);
    }
  } else {
    const defaultDates = { startDate: todayDate, endDate: tomorrowDate };
    setSelectedDates(defaultDates);
    localStorage.setItem("selectedBookingDates", JSON.stringify(defaultDates));
  }
}, [setSelectedDates, todayDate, tomorrowDate]);


  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  useEffect(() => {
    if (selectedDates.startDate && selectedDates.endDate) {
      // Säilytä kokonainen ISO merkkijono
      setStartDate(selectedDates.startDate);
      setEndDate(selectedDates.endDate);
    }
  }, [selectedDates]);

  const handleDateChange = (event) => {
    const { name, value } = event.target;
    if (!value) return;
  
    const selected = new Date(value);
    selected.setHours(0, 0, 0, 0);
    if (selected < now.current) {
      // Estetään menneiden päivien valinta
      toast.error("Et voi valita menneitä päivämääriä.");
      return;
    }
  
    const getTimePart = (isoStr) =>
      new Date(isoStr).toTimeString().split(" ")[0];
  
    if (name === "startDate") {
      const timePart = getTimePart(selectedDates.startDate || new Date().toISOString());
      const newStartISO = new Date(value + "T" + timePart).toISOString();
  
      const newEnd = new Date(newStartISO);
      newEnd.setDate(newEnd.getDate() + 1);
      const newEndISO = newEnd.toISOString();
  
      const updatedDates = { startDate: newStartISO, endDate: newEndISO };
      setSelectedDates(updatedDates);
      localStorage.setItem("selectedBookingDates", JSON.stringify(updatedDates));
      onDatesChange && onDatesChange(updatedDates);
    } else if (name === "endDate") {
      const timePart = getTimePart(selectedDates.endDate || new Date().toISOString());
      const newEndISO = new Date(value + "T" + timePart).toISOString();
      const updatedDates = { startDate: selectedDates.startDate, endDate: newEndISO };
      setSelectedDates(updatedDates);
      localStorage.setItem("selectedBookingDates", JSON.stringify(updatedDates));
      onDatesChange && onDatesChange(updatedDates);
    }
  };
  
  

  const getLabelClasses = () =>
    "block font-customFont1 dark:text-shadow-lg text-left text-darkest dark:text-primary xxsm:text-base text-lg sm:text-lg md:text-lg lg:text-lg xl:text-xl 1xl:text-1xl font-bold mb-2";

  return (
    <div className="grid grid-cols-2 gap-6">
      <div className="flex flex-col">
        <label
          htmlFor="startDate"
          className={getLabelClasses()}
          style={labelColor ? { color: labelColor } : {}}
        >
          Alkaa:
        </label>
        <input
          id="startDate"
          name="startDate"
          type="date"
          value={startDate ? startDate.substring(0, 10) : ""}
          min={todayDate}
          onChange={handleDateChange}
          disabled={isDisabled}
          className="w-full font-customFont1 bg-white dark:bg-darkest dark:text-primary border border-gray-800 rounded-xl shadow-sm focus:outline-none text-center focus:ring-2 focus:ring-primary transition pr-4"
        />
      </div>
      <div className="flex flex-col">
        <label
          htmlFor="endDate"
          className={getLabelClasses()}
          style={labelColor ? { color: labelColor } : {}}
        >
          Loppuu:
        </label>
        <input
          id="endDate"
          name="endDate"
          type="date"
          value={endDate ? endDate.substring(0, 10) : ""}
          min={startDate ? startDate.substring(0, 10) : todayDate}
          onChange={handleDateChange}
          disabled={isDisabled}
          className="w-full font-customFont1 bg-white dark:bg-darkest dark:text-primary border border-gray-800 rounded-xl shadow-sm focus:outline-none text-center focus:ring-2 focus:ring-primary transition pr-4"
        />
      </div>
    </div>
  );
};

RentalDateSelector.propTypes = {
  onDatesChange: PropTypes.func,
  isDisabled: PropTypes.bool,
  labelColor: PropTypes.string,
};

export default RentalDateSelector;
